/**
 * ConfidenceBadge Component (TASK-133)
 *
 * Displays a color-coded confidence indicator for AI-extracted KYC fields.
 * Shows extraction confidence scores to help users validate pre-filled data.
 *
 * Color coding:
 * - Green (≥90%): High confidence - likely accurate
 * - Yellow (75-89%): Medium confidence - should verify
 * - Red (<75%): Low confidence - requires verification
 */

import React from 'react';
import { CheckCircle2, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';

interface ConfidenceBadgeProps {
  /** Confidence score between 0 and 1 */
  confidence: number;
  /** Optional field name for tooltip context */
  fieldName?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Show percentage text */
  showPercentage?: boolean;
  /** Custom className */
  className?: string;
}

export const ConfidenceBadge: React.FC<ConfidenceBadgeProps> = ({
  confidence,
  fieldName,
  size = 'sm',
  showPercentage = true,
  className = ''
}) => {
  // Determine confidence level and styling
  const getConfidenceLevel = () => {
    if (confidence >= 0.90) {
      return {
        level: 'high',
        label: 'High Confidence',
        color: 'text-green-700',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        icon: CheckCircle2,
        description: 'This field was extracted with high confidence and is likely accurate.'
      };
    } else if (confidence >= 0.75) {
      return {
        level: 'medium',
        label: 'Medium Confidence',
        color: 'text-yellow-700',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        icon: AlertTriangle,
        description: 'This field was extracted with medium confidence. Please verify the value.'
      };
    } else {
      return {
        level: 'low',
        label: 'Low Confidence',
        color: 'text-red-700',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: AlertCircle,
        description: 'This field was extracted with low confidence. Please verify and correct if needed.'
      };
    }
  };

  const confidenceInfo = getConfidenceLevel();
  const Icon = confidenceInfo.icon;
  const percentage = Math.round(confidence * 100);

  // Size-based styling
  const sizeClasses = {
    sm: {
      badge: 'text-xs px-2 py-0.5',
      icon: 'w-3 h-3',
      text: 'text-xs'
    },
    md: {
      badge: 'text-sm px-2.5 py-1',
      icon: 'w-3.5 h-3.5',
      text: 'text-sm'
    },
    lg: {
      badge: 'text-base px-3 py-1.5',
      icon: 'w-4 h-4',
      text: 'text-base'
    }
  };

  const sizeClass = sizeClasses[size];

  return (
    <Tooltip.Provider delayDuration={300}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span
            className={`
              inline-flex items-center gap-1.5 rounded-full border
              ${confidenceInfo.bgColor} ${confidenceInfo.borderColor}
              ${sizeClass.badge}
              cursor-help transition-colors hover:shadow-sm
              ${className}
            `}
            role="status"
            aria-label={`${confidenceInfo.label}: ${percentage}%`}
          >
            <Icon className={`${sizeClass.icon} ${confidenceInfo.color}`} />
            {showPercentage && (
              <span className={`font-medium ${confidenceInfo.color} ${sizeClass.text}`}>
                {percentage}%
              </span>
            )}
          </span>
        </Tooltip.Trigger>

        <Tooltip.Portal>
          <Tooltip.Content
            className="z-50 max-w-xs rounded-lg bg-gray-900 px-3 py-2 text-sm text-white shadow-lg"
            sideOffset={5}
          >
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-400" />
                <span className="font-semibold">{confidenceInfo.label}</span>
              </div>

              {fieldName && (
                <div className="text-gray-300 text-xs">
                  Field: {fieldName}
                </div>
              )}

              <div className="text-gray-300">
                {confidenceInfo.description}
              </div>

              <div className="pt-1 border-t border-gray-700 text-xs text-gray-400">
                Extracted with {percentage}% confidence from uploaded documents
              </div>
            </div>

            <Tooltip.Arrow className="fill-gray-900" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

/**
 * Compact version without percentage text - shows only icon
 */
export const ConfidenceIcon: React.FC<Omit<ConfidenceBadgeProps, 'showPercentage'>> = (props) => {
  return <ConfidenceBadge {...props} showPercentage={false} />;
};

/**
 * Helper to check if a field has confidence data
 */
export const hasConfidence = (value: any): value is { value: any; confidence: number } => {
  return (
    value &&
    typeof value === 'object' &&
    'value' in value &&
    'confidence' in value &&
    typeof value.confidence === 'number'
  );
};

/**
 * Helper to extract confidence score from prefill data
 * @param confidenceMap - The _confidence map from prefill data
 * @param fieldPath - Dot-notation path to the field (e.g., "personalInfo.firstName")
 * @returns Confidence score or null if not found
 */
export const getFieldConfidence = (
  confidenceMap: Record<string, number> | undefined,
  fieldPath: string
): number | null => {
  if (!confidenceMap) return null;
  return confidenceMap[fieldPath] ?? null;
};

export default ConfidenceBadge;
